/**
 * NavigationContext — merges role-based profile + per-user overrides.
 *
 * Fetches:
 *   GET /api/v1/aif/me/navigation-profile          (role defaults)
 *   GET /api/v1/aif/users/me/navigation-preferences (user overrides)
 *
 * Exposes merged finalProfile + invalidateNavigation() + updatePreference(key, value).
 * Session-cached via useState. Race-condition-safe via AbortController.
 *
 * A3 Sprint 2.
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../lib/apiClient';

const NavigationContext = createContext(null);

export function NavigationProvider({ children, token }) {
  const [roleProfile, setRoleProfile] = useState(null);
  const [userPreferences, setUserPreferences] = useState({});
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);

  // Fetch both endpoints on mount + on token change
  const fetchAll = useCallback(async (signal) => {
    if (!token) {
      setRoleProfile(null);
      setUserPreferences({});
      return;
    }

    setLoading(true);

    try {
      const [profileRes, prefsRes] = await Promise.allSettled([
        apiClient.get('/api/v1/aif/me/navigation-profile', { signal }),
        apiClient.get('/api/v1/aif/users/me/navigation-preferences', { signal }),
      ]);

      if (profileRes.status === 'fulfilled') {
        if (!signal?.aborted) setRoleProfile(profileRes.value.data);
      }

      if (prefsRes.status === 'fulfilled') {
        if (!signal?.aborted) setUserPreferences(prefsRes.value.data?.preferences || {});
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        // Silent fail — profile stays at last known or null
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Abort any in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    fetchAll(controller.signal);

    return () => controller.abort();
  }, [fetchAll]);

  // Merge: user preferences override role defaults
  const finalProfile = roleProfile
    ? { ...roleProfile, ...userPreferences }
    : null;

  // Re-fetch both sources
  const invalidateNavigation = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    fetchAll(controller.signal);
  }, [fetchAll]);

  // Optimistic update + PATCH to backend
  const updatePreference = useCallback(async (key, value) => {
    // Optimistic
    setUserPreferences(prev => {
      if (value === null) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: value };
    });

    try {
      await apiClient.patch(
        '/api/v1/aif/users/me/navigation-preferences',
        { preferences: { [key]: value } },
        { entityType: 'navigation_preference', queueOnOffline: true }
      );
    } catch {
      // Revert on failure — re-fetch from server
      invalidateNavigation();
    }
  }, [invalidateNavigation]);

  const value = {
    profile: finalProfile,
    roleProfile,
    userPreferences,
    loading,
    invalidateNavigation,
    updatePreference,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigationProfile() {
  const ctx = useContext(NavigationContext);
  if (!ctx) {
    // Fallback for components rendered outside provider (e.g. tests)
    return { profile: null, roleProfile: null, userPreferences: {}, loading: false, invalidateNavigation: () => {}, updatePreference: () => {} };
  }
  return ctx;
}

export default NavigationContext;
